import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Globe2, Coins } from "lucide-react";

const FLAG = { GBP: "🇬🇧", USD: "🇺🇸", CNY: "🇨🇳", JPY: "🇯🇵", EUR: "🇪🇺" };
const SYMBOL = { GBP: "£", USD: "$", CNY: "¥", JPY: "¥", EUR: "€" };

export default function StarCoinPage() {
  const [c, setC] = useState(null);

  useEffect(() => {
    const load = () => api.get("/exchange-rates/starcoin-composition").then((r) => setC(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const components = c ? Object.entries(c.components) : [];
  const total = c?.basket_usd || 0;

  return (
    <div className="min-h-screen text-white">
      <nav className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" data-testid="stc-nav-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#7a5f16] grid place-items-center font-display font-black text-black">★</div>
            <div className="leading-none">
              <div className="font-display text-lg font-bold">BlackStar</div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-white/60">STARCOIN</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <Link to="/status" className="link-underline">Status</Link>
            <Link to="/atm-locator" className="link-underline">Find ATM</Link>
          </div>
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="chip gold mb-4" data-testid="stc-chip">◆ ASSET-REFERENCED · POPULATION-INDEXED</div>
        <h1 className="font-display font-black leading-[0.95] tracking-tighter" style={{fontSize:"clamp(3rem, 7vw, 6rem)"}}>
          One <span className="italic text-[#d4af37]">StarCoin</span>,<br/>backed by five sovereigns.
        </h1>
        <p className="mt-6 text-white/70 max-w-2xl text-lg leading-relaxed">
          Every StarCoin Credit Unit is minted against a fixed reserve basket of five world currencies. Its price rises with global population — the more holders, the greater the coverage.
        </p>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          <div className="panel p-6" data-testid="stc-stat-price">
            <div className="flex items-center gap-2 label"><Coins size={12}/> Price / Credit Unit</div>
            <div className="font-display text-4xl mt-3 text-[#d4af37]">
              ${c ? c.price_usd_per_credit_unit.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2}) : "…"}
            </div>
            <div className="text-xs text-white/50 mt-2 font-mono">Basket ${(total).toLocaleString(undefined,{maximumFractionDigits:2})} · pop ×{c?.population_factor?.toFixed(4) || "…"}</div>
          </div>
          <div className="panel p-6" data-testid="stc-stat-pop">
            <div className="flex items-center gap-2 label"><Users size={12}/> World Population</div>
            <div className="font-display text-4xl mt-3 text-[#00f0ff]">
              {c ? (c.population/1e9).toFixed(3) : "…"}B
            </div>
            <div className="text-xs text-white/50 mt-2 font-mono">baseline 8.000B · 2024-01-01</div>
          </div>
          <div className="panel p-6" data-testid="stc-stat-factor">
            <div className="flex items-center gap-2 label"><Globe2 size={12}/> Expansion Factor</div>
            <div className="font-display text-4xl mt-3 text-[#39ff14]">
              ×{c?.population_factor?.toFixed(6) || "…"}
            </div>
            <div className="text-xs text-white/50 mt-2 font-mono">~0.87% annual population growth</div>
          </div>
        </div>

        <h2 className="font-display text-3xl mt-16 mb-6">Reserve Basket · per Credit Unit</h2>
        <div className="panel p-6">
          {components.map(([code, comp]) => {
            const pct = total > 0 ? (comp.usd_value / total) * 100 : 0;
            return (
              <div key={code} className="py-4 border-b border-white/5 last:border-b-0" data-testid={`stc-comp-${code}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{FLAG[code]}</span>
                    <div>
                      <div className="font-mono text-sm">{code}</div>
                      <div className="text-xs text-white/50">{SYMBOL[code]}{comp.amount.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[#d4af37]">${comp.usd_value.toLocaleString(undefined,{maximumFractionDigits:2})}</div>
                    <div className="text-[10px] text-white/40 font-mono">{pct.toFixed(1)}% · rate {comp.rate_usd}</div>
                  </div>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#00f0ff] transition-all" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-4 mt-3 border-t border-white/10">
            <div className="label">AMALGAMATED BASKET</div>
            <div className="font-display text-2xl text-[#d4af37]" data-testid="stc-basket-total">${total.toLocaleString(undefined,{maximumFractionDigits:2})}</div>
          </div>
        </div>

        <div className="panel p-6 mt-8">
          <div className="label mb-3">Pricing formula</div>
          <pre className="font-mono text-xs text-[#00f0ff] whitespace-pre-wrap leading-relaxed" data-testid="stc-formula">
{`price(STC) = ( 100,000 × rate(GBP→USD)
             + 100,000 × rate(USD→USD)
             + 100,000 × rate(CNY→USD)
             + 10,000,000 × rate(JPY→USD)
             + 100,000 × rate(EUR→USD) )
           × ( current_world_population / 8,000,000,000 )`}
          </pre>
        </div>
      </section>
      <footer className="border-t border-white/10 py-8 mt-6 text-center text-xs text-white/40 font-mono">© 2026 BlackStar Virtual Mint · StarCoin basket-referenced</footer>
    </div>
  );
}
